import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import WaitlistDialog from "@/components/shared/WaitlistDialog";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function WaitlistCountdown() {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 3,
    hours: 8,
    minutes: 16,
    seconds: 0,
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    // Set target date: now + 3 days, 8 hours, 16 minutes
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 3);
    targetDate.setHours(targetDate.getHours() + 8);
    targetDate.setMinutes(targetDate.getMinutes() + 16);

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        clearInterval(timer);
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const TimeCard = ({ value, label }: { value: number; label: string }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center p-4 sm:p-6 rounded-xl bg-white/5 border border-white/10"
    >
      <div className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tabular-nums">
        {value.toString().padStart(2, "0")}
      </div>
      <div className="text-xs sm:text-sm font-normal text-white/50 mt-2 uppercase tracking-wider">{label}</div>
    </motion.div>
  );

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-4"
      style={{
        background: "linear-gradient(180deg, #000000 0%, #0a0a0f 50%, #000000 100%)",
      }}
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: "brightness(0.6)",
          }}
        >
          <source src="https://media.klauyros.artoverse.club/pricingbackground.webm" type="video/webm" />
        </video>
        {/* Dark overlay for better text readability */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.5) 100%)",
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center space-y-12">
        {/* Hero Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4"
        >
          <img
            src="/assets/whitelogo.png"
            alt="Klau y Ros"
            width={256}
            height={160}
            className="h-24 sm:h-28 lg:h-32 w-auto mx-auto mb-4 object-contain"
            loading="eager"
            style={{
              pointerEvents: "none",
              backgroundColor: "transparent",
            }}
          />
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white/90 tracking-wide leading-tight">
            Something Extraordinary
            <br />
            Awaits
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-white/50 max-w-md mx-auto">
            Exclusive early access for those who join first
          </p>
        </motion.div>

        {/* Countdown Timer */}
        <div className="grid grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
          <TimeCard value={timeLeft.days} label="Days" />
          <TimeCard value={timeLeft.hours} label="Hours" />
          <TimeCard value={timeLeft.minutes} label="Minutes" />
          <TimeCard value={timeLeft.seconds} label="Seconds" />
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-white text-black px-8 py-3 rounded-md text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Join Waitlist
          </Button>
        </motion.div>

        {/* Disclaimer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="text-xs text-white/30 max-w-sm mx-auto"
        >
          Limited availability · Early access discount
        </motion.p>
      </div>

      {/* Waitlist Dialog */}
      <WaitlistDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
